import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/contexts/AuthContext';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  ArrowLeft, Loader2, TrendingUp, Camera, FileText, MapPin, MessageSquare, Shield, Sparkles, Info,
} from 'lucide-react';

/** One labelled row + progress bar, matching the mobile "See details" layout. */
function MetricRow({ label, points, weight, color = '#10B981', explainer }) {
  const pts = Number(points || 0);
  const w = Number(weight || 1);
  const pct = Math.max(0, Math.min(100, (pts / w) * 100));
  // Show 1 decimal only when needed (matches mobile: "3.2/20" but "17/20").
  const display = Number.isInteger(pts) ? `${pts}` : pts.toFixed(1);
  const testId = `metric-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
  return (
    <div className="flex flex-col gap-1.5" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <span className="text-[15px] font-semibold text-zinc-900">{label}</span>
          {explainer && (
            <Popover>
              <PopoverTrigger asChild>
                <button
                  data-testid={`${testId}-info`}
                  className="text-zinc-400 hover:text-zinc-700 transition-colors"
                  aria-label={`How ${label} is calculated`}
                >
                  <Info size={14} strokeWidth={2} />
                </button>
              </PopoverTrigger>
              <PopoverContent
                side="top"
                align="start"
                className="w-[280px] p-3.5 text-[12.5px] leading-relaxed text-zinc-700 bg-white border-black/[0.08] shadow-lg"
              >
                <p className="text-[11px] font-bold uppercase tracking-wider text-zinc-900 mb-1.5">
                  How this is calculated
                </p>
                {explainer}
              </PopoverContent>
            </Popover>
          )}
        </div>
        <span className="text-[14px] text-zinc-700">
          <span className="font-bold text-zinc-900">{display}</span>
          <span className="text-zinc-500"> / {w}</span>
        </span>
      </div>
      <div className="h-1.5 w-full rounded-full bg-black/[0.06] overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{ width: `${pct}%`, background: color }}
        />
      </div>
    </div>
  );
}

/**
 * "See details"-style card. `tone` picks the mint (green) vs. lilac (purple) style.
 */
function ScoreDetailsCard({ tone, title, subtitle, statusLabel, metrics, testId }) {
  const isMint = tone === 'mint';
  const cardBg = isMint ? '#DCFCE7' : '#EDE9FE';
  const iconRingBg = isMint ? 'rgba(0,0,0,0.05)' : 'rgba(0,0,0,0.05)';
  const iconColor = isMint ? '#047857' : '#7E22CE';
  const pillBg = isMint ? 'rgba(16,185,129,0.20)' : 'rgba(139,92,246,0.20)';
  const pillFg = isMint ? '#047857' : '#7E22CE';
  const barColor = isMint ? '#10B981' : '#8B5CF6';
  return (
    <div className="rounded-2xl overflow-hidden" data-testid={testId}>
      {/* Header block — coloured background */}
      <div style={{ background: cardBg, padding: '20px' }} className="flex items-start gap-4">
        <div
          className="w-[76px] h-[76px] rounded-full flex items-center justify-center flex-shrink-0"
          style={{ background: iconRingBg, border: '1px solid rgba(0,0,0,0.05)' }}
        >
          <Sparkles size={26} style={{ color: iconColor }} strokeWidth={2} />
        </div>
        <div className="min-w-0">
          <h3 className="text-[22px] font-black text-zinc-900 leading-tight">{title}</h3>
          <p className="text-[13.5px] text-zinc-700 mt-1 leading-snug">{subtitle}</p>
          <span
            className="inline-block mt-2 rounded-full font-bold"
            style={{ background: pillBg, color: pillFg, padding: '5px 12px', fontSize: 12.5 }}
          >
            {statusLabel}
          </span>
        </div>
      </div>
      {/* Metrics list — white body */}
      <div className="bg-white p-5 space-y-4">
        {metrics.map((m) => (
          <MetricRow key={m.label} label={m.label} points={m.points} weight={m.weight} color={barColor} explainer={m.explainer} />
        ))}
      </div>
    </div>
  );
}

function bandLabel(v2, kind /* 'meet' | 'resp' */) {
  if (!v2) return '—';
  if (v2.is_new) return kind === 'meet' ? 'Building history' : 'Not enough activity yet';
  return v2.band || '—';
}

export default function ProfileOptimizer() {
  const navigate = useNavigate();
  const [tips, setTips] = useState(null);
  const [v2, setV2] = useState(null);          // /v2/scores/me
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/profile-score').then(r => setTips(r.data)).catch(() => {}),
      api.get('/v2/scores/me').then(r => setV2(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-7 h-7 animate-spin text-[#EF4444]" />
      </div>
    );
  }

  const mb = v2?.meet_breakdown || {};
  const rb = v2?.responsiveness_breakdown || {};

  const meetMetrics = [
    { label: 'Profile readiness',   points: mb.profile_readiness?.points,  weight: mb.profile_readiness?.weight  || 20,
      explainer: 'Points for a complete profile: photos, bio, key questions filled in. Add any missing pieces to raise this.' },
    { label: 'Verification & trust',points: mb.verification_trust?.points, weight: mb.verification_trust?.weight || 20,
      explainer: 'Photo verification, phone verification, and any trust badges you\'ve earned. Verify your photos and phone for full credit.' },
    { label: 'Engagement',          points: mb.engagement_comm?.points,    weight: mb.engagement_comm?.weight    || 20,
      explainer: 'How actively you swipe, match, and start conversations. Consistent daily activity keeps this high.' },
    { label: 'Date readiness',      points: mb.date_readiness?.points,     weight: mb.date_readiness?.weight     || 20,
      explainer: 'Whether your Meet Preference matches, Preview Availability is set, and Safe Meet is used. Signals you\'re ready to actually meet.' },
    { label: 'Reliability',         points: mb.reliability?.points,        weight: mb.reliability?.weight        || 20,
      explainer: 'Whether you\'ve reliably shown up for scheduled meets in the past. Attend the meets you accept and this grows over time.' },
  ];

  const replyMetrics = [
    { label: 'Reply rate',       points: rb.reply_rate?.points,            weight: rb.reply_rate?.weight            || 35,
      explainer: 'The percentage of matches you\'ve replied to at least once. Reply to more of your matches, even briefly, to raise this.' },
    { label: 'Consistency',      points: rb.response_consistency?.points,  weight: rb.response_consistency?.weight  || 20,
      explainer: 'How steady your reply patterns are across matches. Replying regularly (not just to some) rewards this score.' },
    { label: 'Response time',    points: rb.response_time?.points,         weight: rb.response_time?.weight         || 15,
      explainer: 'How quickly you reply to new messages, on average. Same-day replies score highest.' },
    { label: 'Follow-through',   points: rb.follow_through?.points,        weight: rb.follow_through?.weight        || 15,
      explainer: 'Whether you keep conversations going past the first few messages. Depth over ghost.' },
    { label: 'Scheduled meets',  points: rb.scheduled_reliability?.points, weight: rb.scheduled_reliability?.weight || 15,
      explainer: 'Whether you follow through on Safe Meets you accept. Showing up (or cancelling well in advance) keeps this high.' },
  ];

  const tipIcons = { Photos: Camera, Bio: FileText, Questions: MessageSquare, Location: MapPin, Verification: Shield };
  const priorityColors = { high: '#EF4444', medium: '#EAB308', low: '#3B82F6' };

  return (
    <div className="min-h-screen bg-white pb-[80px]" data-testid="profile-optimizer-page">
      <div className="px-5 pt-6 pb-3 flex items-center gap-3">
        <button onClick={() => navigate(-1)} data-testid="profile-optimizer-back" className="text-zinc-500 hover:text-zinc-900">
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-xl font-bold">See details</h1>
      </div>

      <div className="px-5 space-y-6">
        {v2 && (
          <>
            <ScoreDetailsCard
              tone="mint"
              testId="meet-score-card"
              title="Meet Score"
              subtitle="How ready you are to meet in person"
              statusLabel={bandLabel(v2.meet, 'meet')}
              metrics={meetMetrics}
            />

            <ScoreDetailsCard
              tone="lilac"
              testId="reply-reliability-card"
              title="Reply Reliability"
              subtitle="How consistently you keep conversations going"
              statusLabel={bandLabel(v2.responsiveness, 'resp')}
              metrics={replyMetrics}
            />
          </>
        )}

        {/* Tips */}
        {tips?.tips?.length > 0 && (
          <div>
            <p className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider mb-3 px-1">
              Improvement tips
            </p>
            <div className="space-y-2">
              {tips.tips.map((tip, i) => {
                const Icon = tipIcons[tip.area] || TrendingUp;
                return (
                  <div
                    key={i}
                    className="bg-[#F8F9FA] border border-black/[0.06] rounded-xl p-4 flex items-start gap-3"
                    data-testid={`tip-${i}`}
                  >
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ background: `${priorityColors[tip.priority]}15` }}
                    >
                      <Icon size={16} style={{ color: priorityColors[tip.priority] }} />
                    </div>
                    <div>
                      <p className="text-xs font-semibold mb-0.5" style={{ color: priorityColors[tip.priority] }}>
                        {tip.area}
                      </p>
                      <p className="text-sm text-zinc-600">{tip.tip}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
